import React, { useCallback, useEffect, useState } from "react";
import InputText from "../../../../components/inputs/InputText";
import { searchProductForRelease } from "../../../../api/inventoryService";
import InputDropSearch from "../../../../components/inputs/InputDropSearch";
import { toast } from "react-toastify";
import { AddReleaseType, ReleaseLine } from "./releaseTypes";

/**
 * Pick one product for a release. Priced (patient / staff sale): unit price
 * prefills from the catalogue and can be changed. Unpriced (clinic use):
 * product and quantity only.
 */
const ReleaseProductModal: React.FC<ReleaseProductModalProps> = ({
  title,
  releaseType,
  priced = true,
  closeModal,
  addProduct,
}) => {
  const [formData, setformData] = useState<ReleaseLine>({
    productId: "",
    unitPrice: 0,
    quantity: 1,
    total: 0,
    discount: 0,
    netTotal: 0,
    productName: "",
    scale: "",
    availableQuantity: 0,
  });
  const [product, setProduct] = useState<Item[]>([]);

  const handleChange = useCallback((e: any) => {
    const { name, value, valueTitle, value2 } = e.target;

    setformData((prevData) => {
      if (name === "productId") {
        return {
          ...prevData,
          [name]: value,
          productName: valueTitle,
          scale: value2?.scale,
          availableQuantity: value2?.availableQuantity,
          unitPrice: parseInt(value2?.price),
          total: prevData.quantity * parseInt(value2?.price),
          netTotal:
            prevData.quantity * parseInt(value2?.price) - prevData.discount,
        };
      } else if (name === "unitPrice") {
        return {
          ...prevData,
          [name]: value,
          total: prevData.quantity * value,
          netTotal: prevData.quantity * value - prevData.discount,
        };
      } else if (name === "quantity") {
        return {
          ...prevData,
          [name]: value,
          total: prevData.unitPrice * value,
          netTotal: prevData.unitPrice * value - prevData.discount,
        };
      } else if (name === "discount") {
        return {
          ...prevData,
          [name]: value,
          netTotal: prevData.total - value,
        };
      } else {
        return { ...prevData, [name]: value };
      }
    });
  }, []);
  const getProductData = useCallback(async (search: string) => {
    try {
      // only products whose category allows this release
      const data = await searchProductForRelease(search, releaseType);
      setProduct(data as Item[]);
    } catch (error) {
      console.error(error);
    }
  }, [releaseType]);

  useEffect(() => {
    getProductData("");
  }, [getProductData]);

  const searchProduct = useCallback(
    (search: string) => {
      getProductData(search);
    },
    [getProductData]
  );
  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const quantity = Number(formData.quantity);

      if (!formData.productId) {
        toast.error("Choose a product.");
      } else if (!formData.availableQuantity || formData.availableQuantity <= 0) {
        toast.error("Out of stock.");
      } else if (!Number.isInteger(quantity) || quantity <= 0) {
        toast.error("Quantity must be a whole number above 0.");
      } else if (quantity > formData.availableQuantity) {
        toast.error(`Only ${formData.availableQuantity} in stock.`);
      } else if (priced && Number(formData.discount) > Number(formData.total)) {
        toast.error("Discount can't be more than the total.");
      } else {
        addProduct({
          ...formData,
          quantity,
          unitPrice: Number(formData.unitPrice) || 0,
          discount: Number(formData.discount) || 0,
          total: Number(formData.total) || 0,
          netTotal: Number(formData.netTotal) || 0,
        });
      }
    },
    [addProduct, formData, priced]
  );
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-600 flex items-center justify-between">
          <h2 className="text-xs font-bold text-slate-800 dark:text-slate-100">{title}</h2>
          <button
            onClick={closeModal}
            className="w-6 h-6 rounded-md bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 transition-colors"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-3 space-y-3">
          <InputDropSearch
            data={product}
            name="productId"
            label="Product Name"
            selectName1="productName"
            selectName2="availableQuantity"
            noMgs="no"
            extraMsg=" stock"
            selectValue="_id"
            onChange={handleChange}
            onSearch={searchProduct}
          />

          {formData.availableQuantity > 0 && (
            <p className="text-xs text-emerald-600 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/40 px-2.5 py-1.5 rounded-lg">
              Available: {formData.availableQuantity} {formData.scale}
            </p>
          )}

          {priced ? (
            <>
              <div className="grid grid-cols-2 gap-2.5">
                <InputText name="unitPrice" label="Unit Price" value={formData.unitPrice} onChange={handleChange} type="number" />
                <InputText name="quantity" label="Quantity" value={formData.quantity} onChange={handleChange} />
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <InputText name="total" label="Total" value={formData.total} onChange={handleChange} isDisable={true} />
                <InputText name="discount" label="Discount" value={formData.discount} onChange={handleChange} />
              </div>

              <div className="p-2.5 bg-emerald-50 dark:bg-emerald-900/40 rounded-lg">
                <p className="text-xs text-emerald-700 dark:text-emerald-300">
                  Net Total: <span className="font-bold text-sm">{formData.netTotal}</span>
                </p>
              </div>
            </>
          ) : (
            <InputText name="quantity" label="Quantity" value={formData.quantity} onChange={handleChange} />
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={closeModal}
              className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-medium hover:shadow-lg hover:shadow-primary/20 transition-all text-xs"
            >
              Add Product
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReleaseProductModal;
interface Item {
  [key: string]: any;
}
interface ReleaseProductModalProps {
  /** Which release this is; the product list follows the categories' settings. */
  releaseType: AddReleaseType;
  title: string;
  priced?: boolean;
  closeModal: () => void;
  addProduct: (product: ReleaseLine) => void;
}
